import {IUpdatable} from '@gongxigames/framework/core/types';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';
import {
    Spine as PIXISpine,
    ISkeletonData,
    IAnimationStateListener,
    SpineSprite,
    IAnimationState
} from 'pixi-spine';
import {Skin} from '@pixi-spine/runtime-4.1';

export interface AnimationProperty {
    animationName: string;
    trackIndex?: number;
    loop?: boolean;
    listeners?: IAnimationStateListener;
    skinName?: string;
}

// as Spine has generalized types typescript/IDE don't recognize addListener as method of Spine.state
interface InternalAnimationState extends IAnimationState {
    addListener(listener: IAnimationStateListener): void;
}

interface ISpine extends IUpdatable {
    state: InternalAnimationState;
}

type AnimationCallBack = () => void;

type AnimationEventHandler = {
    callback: AnimationCallBack;
    once: boolean;
};

export class Spine extends PIXISpine implements ISpine {

    protected _updateManager?: UpdateManager;
    protected _isPlaying = false;
    protected _animationEventListeners: Record<string, AnimationEventHandler[]> = {};
    private preventRenderMaskList: SpineSprite[] = [];

    constructor(spineData: ISkeletonData, initialAnimation?: AnimationProperty) {
        super(spineData);

        this.name = `spine_${spineData.name}`;
        this.skeleton.setToSetupPose();
        this.autoUpdate = false;

        if (initialAnimation) {
            this.setAnimation(initialAnimation);
        }

        this.makeNamesForSlotsContainers();
    }

    public start(): Spine {
        this._isPlaying = true;

        return this;
    }

    public stop(): Spine {
        this._isPlaying = false;
        this._updateManager && this._updateManager.remove(this);

        return this;
    }

    public pause(): Spine {
        this._isPlaying = !this._isPlaying;

        return this;
    }

    /**
     * The function sets the animation to play.
     * @param property - animation property
     * @return Spine
     */
    public setAnimation(property: AnimationProperty): Spine {
        if (property.skinName) {
            this.setSkinByName(property.skinName);
        }

        this.state.setAnimation(property.trackIndex || 0, property.animationName, !!property.loop);

        if (property.listeners) {
            this.state.addListener(property.listeners);
        }

        this.state.addListener({
            event: (trackEntry, event) => {
                const events = this._animationEventListeners[event.data.name];

                if (!events) {
                    return;
                }

                for (let i = 0; i < events.length; i++) {
                    const handler = events[i];

                    handler.callback();

                    if (handler.once) {
                        events.splice(i, 1);
                        i--;
                    }
                }
            }
        });

        this.update(0);

        return this;
    }

    /**
     * The function sets the skin to use during animation.
     * @param skinName - skin name
     * @return Spine
     */
    // according to https://github.com/pixijs/spine/blob/master/examples/change_skin.md
    public setSkinByName(skinName: string): Spine {
        // @ts-ignore
        this.skeleton.setSkin(null);
        this.skeleton.setSkinByName(skinName);

        return this;
    }

    /**
     * The function sets the combined skin to use during animation.
     * @param baseSkinName - base skin name
     * @param partialSkinNames - partial skin names
     * @return Spine
     */
    // according to https://github.com/pixijs/spine/blob/master/examples/change_skin.md
    public setCombinedSkin(baseSkinName: string, partialSkinNames: string[]): Spine {
        const newSkin = new Skin('combined-skin');

        newSkin.addSkin(this.spineData.findSkin(baseSkinName));

        partialSkinNames.forEach((skinName) => {
            newSkin.addSkin(this.spineData.findSkin(skinName));
        });

        this.skeleton.setSkin(newSkin);
        this.skeleton.setSlotsToSetupPose();

        return this;
    }

    public update(dt: number): void {
        super.update(dt / 1000);

        this.preventRenderMaskList.forEach((item) => {
            if (item.isMask && item.renderable) {
                item.renderable = false;
            }
        });
    }

    public onAnimationEvent(name: string, callback: AnimationCallBack): Spine {
        const collection = this._animationEventListeners[name] ||= [];

        collection.push({callback, once: false});

        return this;
    }

    public offAnimationEvent(name: string, callback: AnimationCallBack): Spine {
        const index = this._animationEventListeners[name]
            ?.findIndex(item => item.callback === callback) ?? -1;

        if (index === -1) {
            return this;
        }

        this._animationEventListeners[name].splice(index, 1);

        return this;
    }

    public onceAnimationEvent(name: string, callback: AnimationCallBack): Spine {
        const collection = this._animationEventListeners[name] ||= [];

        collection.push({callback, once: true});

        return this;
    }

    public clearAllAnimationEvents(): Spine {
        this._animationEventListeners = {};

        return this;
    }

    public promiseOnAnimationEvent(name: string): Promise<void> {
        return new Promise ((res: () => void) => {
            const callback = (): void => {
                this.offAnimationEvent(name, callback);
                res();
            };

            this.onAnimationEvent(name, callback);
        });
    }

    public get updateManager(): UpdateManager | undefined {
        return this._updateManager;
    }

    public set updateManager(value: UpdateManager | undefined) {
        this._updateManager = value;
    }

    public get isPlaying(): boolean {
        return this._isPlaying;
    }

    public addToPreventRenderMaskList(item: SpineSprite): void {
        if (!this.preventRenderMaskList.includes(item)) {
            this.preventRenderMaskList.push(item);
        }
    }

    protected makeNamesForSlotsContainers(): void {
        this.skeleton.slots.forEach( slot => {
            if (slot.currentSprite) {
                slot.currentSprite.name = slot.currentSpriteName;
                slot.currentSprite.parent.name = slot.currentSpriteName;
            }
        });
    }

    [AUTOMATION_META](): AutomationMeta {
        const animations = this.state.tracks.map(item => item.animation.name as string);

        return {
            type: 'spine',
            isPlaying: this._isPlaying,
            skin: this.skeleton.skin.name,
            animations
        };
    }
}
